// #tag: STUDENT VAULT MUTATIONS HOOK
// #purpose: Encapsulates document mutations (upload, replace, rename, delete, bulk delete, reorder) with automated React Query cache invalidation.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  addVaultCategoryItem,
  deleteVaultDocument,
  deleteVaultDocuments,
  renameVaultDocument,
  replaceVaultDocument,
  reorderVaultDocument,
  uploadVaultDocument,
  type UploadVaultDocumentInput,
} from '../api/vault';

export function useVaultMutations() {
  const queryClient = useQueryClient();

  const invalidateDocs = () => {
    queryClient.invalidateQueries({ queryKey: ['vault-documents'] });
  };

  const invalidateCategories = () => {
    queryClient.invalidateQueries({ queryKey: ['vault-categories'] });
  };

  const upload = useMutation({
    mutationFn: (input: UploadVaultDocumentInput) => uploadVaultDocument(input),
    onSuccess: invalidateDocs,
  });

  const replace = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<UploadVaultDocumentInput> }) =>
      replaceVaultDocument(id, input),
    onSuccess: invalidateDocs,
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameVaultDocument(id, name),
    onSuccess: invalidateDocs,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteVaultDocument(id),
    onSuccess: invalidateDocs,
  });

  const removeMany = useMutation({
    mutationFn: (ids: string[]) => deleteVaultDocuments(ids),
    onSuccess: invalidateDocs,
  });

  const reorder = useMutation({
    mutationFn: ({ id, position }: { id: string; position: number }) =>
      reorderVaultDocument(id, position),
    onSuccess: invalidateDocs,
  });

  const addCategoryItem = useMutation({
    mutationFn: ({ categoryId, item }: { categoryId: string; item: string }) =>
      addVaultCategoryItem(categoryId, item),
    onSuccess: invalidateCategories,
  });

  return {
    upload,
    replace,
    rename,
    remove,
    removeMany,
    reorder,
    addCategoryItem,
  };
}
